import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { WishlistProvider } from "./context/WishlistContext";
import { CartProvider } from "./context/CartContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import Header from "./components/Header";
import VerifyEmailBanner from "./components/VerifyEmailBanner";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

// Lazy load non-critical pages for better performance
const ShopPage = lazy(() => import("./pages/ShopPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const OrderConfirmationPage = lazy(() => import("./pages/OrderConfirmationPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const DeliveryPage = lazy(() => import("./pages/DeliveryPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AddressesPage = lazy(() => import("./pages/AddressesPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const SearchResultsPage = lazy(() => import("./pages/SearchResultsPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const TrackOrderPage = lazy(() => import("./pages/TrackOrderPage"));
const GiftCardsPage = lazy(() => import("./pages/GiftCardsPage"));
const BundlesPage = lazy(() => import("./pages/BundlesPage"));
const BundleDetailPage = lazy(() => import("./pages/BundleDetailPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));

// Collections pages
const CollectionsPage = lazy(() => import("./pages/CollectionsPage").then(m => ({ default: m.CollectionsPage })));
const CollectionDetailPage = lazy(() => import("./pages/CollectionsPage").then(m => ({ default: m.CollectionDetailPage })));

// Promo pages
const PromosPage = lazy(() => import("./pages/PromosPage").then(m => ({ default: m.PromosPage })));
const PromoDetailPage = lazy(() => import("./pages/PromosPage").then(m => ({ default: m.PromoDetailPage })));

// Lazy load legal pages
const LegalPages = lazy(() => import("./pages/LegalPages"));
const PrivacyPolicy = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.PrivacyPolicy })));
const TermsConditions = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.TermsConditions })));
const ReturnsPolicy = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.ReturnsPolicy })));
const DeliveryPolicy = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.DeliveryPolicy })));
const HygienePolicy = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.HygienePolicy })));
const Disclaimer = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.Disclaimer })));

// Lazy load chatbot to improve initial load
const ChatbotWidget = lazy(() => import("./components/ChatbotWidget"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center" role="status" aria-label="Loading page">
    <Loader2 className="h-8 w-8 animate-spin text-gray-400" aria-hidden="true" />
    <span className="sr-only">Loading...</span>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WishlistProvider>
          <CartProvider>
            <CurrencyProvider>
              <div className="App min-h-screen flex flex-col">
                <Routes>
                  {/* Admin route without header/footer */}
                  <Route path="/admin" element={
                    <Suspense fallback={<PageLoader />}>
                      <AdminDashboard />
                    </Suspense>
                  } />
                  
                  {/* Track order page (public, has own header/footer) */}
                  <Route path="/track-order" element={
                    <Suspense fallback={<PageLoader />}>
                      <TrackOrderPage />
                    </Suspense>
                  } />
                  
                  {/* Main routes with header/footer */}
                  <Route path="*" element={
                    <>
                      <a
                        href="#main-content"
                        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-stone-900 focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg"
                      >
                        Skip to main content
                      </a>
                      <Header />
                      <VerifyEmailBanner />
                      <main id="main-content" className="flex-1" role="main">
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            <Route path="/" element={<HomePage />} />
                            
                            {/* Shop routes */}
                            <Route path="/shop" element={<ShopPage />} />
                            <Route path="/shop/:category" element={<ShopPage />} />
                            <Route path="/shop/:category/:subcategory" element={<ShopPage />} />
                            
                            {/* Offers routes */}
                            <Route path="/offers" element={<ShopPage filter="sale" />} />
                            <Route path="/offers/:category" element={<ShopPage filter="sale" />} />
                            
                            {/* New arrivals routes */}
                            <Route path="/new" element={<ShopPage filter="new" />} />
                            <Route path="/new/:category" element={<ShopPage filter="new" />} />
                            
                            {/* Bestsellers */}
                            <Route path="/bestsellers" element={<ShopPage filter="bestseller" />} />
                            
                            {/* Concerns routes */}
                            <Route path="/concerns/:concern" element={<ShopPage />} />
                            <Route path="/shop/concern/:slug" element={<ShopPage filterType="concern" />} />
                            <Route path="/shop/concern" element={<ShopPage filterType="concerns-list" />} />
                            
                            {/* Trending & other */}
                            <Route path="/trending" element={<ShopPage filter="featured" />} />
                            <Route path="/trending/:type" element={<ShopPage filter="featured" />} />
                            
                            {/* Collections */}
                            <Route path="/collections" element={<CollectionsPage />} />
                            <Route path="/collections/:slug" element={<CollectionDetailPage />} />

                            {/* Promos */}
                            <Route path="/promos" element={<PromosPage />} />
                            <Route path="/promos/:slug" element={<PromoDetailPage />} />
                            
                            <Route path="/product/:id" element={<ProductDetailPage />} />
                            <Route path="/cart" element={<CartPage />} />
                            <Route path="/checkout" element={<CheckoutPage />} />
                            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                            <Route path="/order-confirmation/:id" element={<OrderConfirmationPage />} />
                            <Route path="/verify-email" element={<VerifyEmailPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/delivery" element={<DeliveryPage />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/register" element={<RegisterPage />} />
                            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                            <Route path="/reset-password" element={<ResetPasswordPage />} />
                            <Route path="/account" element={<AccountPage />} />
                            <Route path="/account/addresses" element={<AddressesPage />} />
                            <Route path="/wishlist" element={<WishlistPage />} />
                            <Route path="/search" element={<SearchResultsPage />} />
                            <Route path="/blog" element={<BlogListPage />} />
                            <Route path="/blog/:slug" element={<BlogPostPage />} />
                            <Route path="/gift-cards" element={<GiftCardsPage />} />
                            
                            {/* Legal Pages */}
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                            <Route path="/terms-conditions" element={<TermsConditions />} />
                            <Route path="/returns-policy" element={<ReturnsPolicy />} />
                            <Route path="/delivery-policy" element={<DeliveryPolicy />} />
                            <Route path="/hygiene-policy" element={<HygienePolicy />} />
                            <Route path="/disclaimer" element={<Disclaimer />} />
                            <Route path="/faq" element={<FAQPage />} />
                            <Route path="/bundles" element={<BundlesPage />} />
                            <Route path="/bundles/:id" element={<BundleDetailPage />} />
                          </Routes>
                        </Suspense>
                      </main>
                      <Footer />
                    </>
                  } />
                </Routes>
                <Toaster />
                <Suspense fallback={null}>
                  <ChatbotWidget />
                </Suspense>
              </div>
            </CurrencyProvider>
          </CartProvider>
        </WishlistProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
